import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QrCode, UserRound, ShieldCheck, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { ParishMark, inputCls, StatusBadge } from "../components/common";
import { pApi, fmtDate, fmtClock } from "../lib/api";
import { LangToggle } from "../lib/i18n";

const HERO = "/hero.jpg";

export default function Home() {
  const [open, setOpen] = useState([]);
  const [code, setCode] = useState("");
  const nav = useNavigate();
  useEffect(() => { pApi.get("/open-meetings").then((r) => setOpen(r.data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="flex items-center justify-between px-5 sm:px-10 py-5">
        <ParishMark />
        <div className="hidden sm:block"><LangToggle /></div>
      </header>
      <section className="px-5 sm:px-10 pb-12 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div className="fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C85A32] mb-4">Diocese of Marbel · SFAP-AMIS</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black text-[#2D3B2D] leading-[1.08] tracking-tight">
            Attendance Management <span className="text-[#5C3A21]">Information System</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-stone-700 max-w-xl leading-relaxed">
            A simple, welcoming way for parishioners, GKK and ministry officers, and our guests to record their presence at parish meetings — in the spirit of Franciscan simplicity.
          </p>
          <form className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl" onSubmit={(e) => { e.preventDefault(); if (code.trim()) nav(`/m/${code.trim()}`); }}>
            <input className={`${inputCls} h-14 text-lg`} placeholder="Meeting ID (e.g. MTG-2026-0001)" value={code} onChange={(e) => setCode(e.target.value)} data-testid="home-meeting-code-input" />
            <button className="btn-big btn-primary whitespace-nowrap" data-testid="home-meeting-code-btn">Go <ArrowRight className="h-5 w-5" /></button>
          </form>
          <p className="mt-2 text-sm text-stone-500 flex items-center gap-2"><QrCode className="h-4 w-4" /> Or simply scan the QR code displayed at the meeting venue.</p>
        </div>
        <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3] border border-[#E5DEC9] fade-up" style={{ animationDelay: "120ms" }}>
          <img src={HERO} alt="Parish community gathering" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B2D]/80 via-transparent to-transparent" />
          <p className="absolute bottom-5 left-6 right-6 font-serif text-white text-lg italic">“Where there is charity and wisdom, there is neither fear nor ignorance.” — St. Francis</p>
        </div>
      </section>
      {open.length > 0 && (
        <section className="px-5 sm:px-10 pb-12">
          <h2 className="font-serif text-xl font-bold text-[#2D3B2D] mb-4">Registration open now</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {open.map((m) => (
              <Link key={m.id} to={`/m/${m.id}`} className="sfap-card p-5 hover:border-[#2D3B2D] transition-colors" data-testid={`open-meeting-${m.code}`}>
                <div className="flex justify-between gap-2 mb-2"><span className="text-xs font-mono text-stone-500">{m.code}</span><StatusBadge status={m.status} /></div>
                <div className="font-serif font-bold text-lg text-stone-900">{m.title}</div>
                <div className="mt-2 text-sm text-stone-600 flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(m.date)} · {fmtClock(m.start_time)}</div>
                <div className="text-sm text-stone-600 flex items-center gap-2"><MapPin className="h-4 w-4" />{m.venue}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="px-5 sm:px-10 pb-16 grid gap-4 md:grid-cols-3">
        <PortalCard to="/me" icon={UserRound} title="Participant Profile" text="Review your information and attendance history with your Participant ID and PIN." testid="portal-participant" />
        <PortalCard to="/login" icon={ShieldCheck} title="Administrator & Officers" text="Parish Administrator, attendance officers and authorized monitors sign in here." testid="portal-staff" />
        <PortalCard to="/station" icon={QrCode} title="Registration Station" text="Prepared offline attendance device for the registration desk." testid="portal-station" />
      </section>
      <footer className="border-t border-[#E5DEC9] px-5 sm:px-10 py-6 text-xs text-stone-500 flex flex-wrap justify-between gap-2">
        <span>Saint Francis of Assisi Parish · Barangay Mabuhay, General Santos City · Diocese of Marbel</span>
        <span>Personal data handled under the Data Privacy Act of 2012 (RA 10173)</span>
      </footer>
    </div>
  );
}

const PortalCard = ({ to, icon: Icon, title, text, testid }) => (
  <Link to={to} data-testid={testid} className="sfap-card p-6 group hover:-translate-y-0.5 transition-transform">
    <Icon className="h-6 w-6 text-[#C85A32]" />
    <div className="font-serif font-bold text-lg mt-3 text-[#2D3B2D] flex items-center gap-2">{title} <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
    <p className="text-sm text-stone-600 mt-1">{text}</p>
  </Link>
);
